import { IdentityCardStateChip } from '@/features/admin/components/IdentityCardStateChip';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import TablePagination from '@mui/material/TablePagination';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid2';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import VerifiedIcon from '@mui/icons-material/Verified';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Stack from '@mui/material/Stack';
import { adminApi, adminKeys } from '@/features/admin/api/admin.api';
import type { AdminGuideListItem } from '@/features/admin/api/admin.api';
import { AdminPageHeader } from '@/features/admin/components/AdminPageHeader';
import { GuideStatusChip } from '@/features/admin/components/GuideStatusChip';
import { StatCard } from '@/features/admin/components/StatCard';
import { DeleteAccountDialog } from '@/features/admin/components/DeleteAccountDialog';
import { StatusBadge } from '@/shared/components/ui/StatusBadge';
import { ADMIN_ROUTES } from '@/features/admin/constants/routes';
import { GuideStatus } from '@/types';

function formatDate(dateStr?: string) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function AdminGuidesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [statusFilter, setStatusFilter] = useState('');
  const [pendingDelete, setPendingDelete] = useState<AdminGuideListItem | null>(null);

  const openReview = (profileId: string) => {
    navigate(ADMIN_ROUTES.review(profileId), { state: { from: ADMIN_ROUTES.GUIDES } });
  };

  const { data: stats } = useQuery({
    queryKey: adminKeys.dashboard(),
    queryFn: adminApi.getDashboard,
  });

  const { data, isLoading, isError } = useQuery({
    queryKey: adminKeys.guides({ page: page + 1, limit: rowsPerPage, status: statusFilter }),
    queryFn: () =>
      adminApi.listGuides({
        page: page + 1,
        limit: rowsPerPage,
        status: statusFilter || undefined,
        sortBy: 'updatedAt',
        sortOrder: 'desc',
      }),
  });

  // Server-side switch (ADMIN_DELETE_ENABLED). Absent/false hides the button.
  const canDelete = stats?.capabilities?.canDeleteAccounts === true;

  const deleteMutation = useMutation({
    mutationFn: (profileId: string) => adminApi.deleteGuide(profileId),
    onSuccess: () => {
      setPendingDelete(null);
      // Removing the last row of a trailing page would leave an empty table.
      if (data?.data?.length === 1 && page > 0) setPage((current) => current - 1);
      // A deleted guide also leaves the review queues and the counters.
      queryClient.invalidateQueries({ queryKey: adminKeys.all });
    },
  });

  const deleteErrorMessage = deleteMutation.isError
    ? ((deleteMutation.error as { response?: { data?: { message?: string } } })?.response?.data
        ?.message ?? 'Failed to delete this guide. Please try again.')
    : null;

  const guideName = (guide: AdminGuideListItem) =>
    guide.fullName ||
    [guide.user?.firstName, guide.user?.lastName].filter(Boolean).join(' ') ||
    'this guide';

  const approvedCount = stats?.guidesByStatus?.[GuideStatus.APPROVED] ?? 0;
  const pendingCount =
    (stats?.guidesByStatus?.[GuideStatus.SUBMITTED] ?? 0) +
    (stats?.guidesByStatus?.[GuideStatus.UNDER_REVIEW] ?? 0);

  return (
    <Box>
      <AdminPageHeader
        title="All Guides"
        subtitle="Every guide profile on the platform, across all verification states"
        actions={
          <TextField
            select
            label="Filter by status"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 220 }}
            size="small"
          >
            <MenuItem value="">All statuses</MenuItem>
            <MenuItem value={GuideStatus.APPROVED}>Approved</MenuItem>
            <MenuItem value={GuideStatus.SUBMITTED}>Submitted</MenuItem>
            <MenuItem value={GuideStatus.UNDER_REVIEW}>Under review</MenuItem>
            <MenuItem value={GuideStatus.REJECTED}>Rejected</MenuItem>
            <MenuItem value={GuideStatus.SUSPENDED}>Suspended</MenuItem>
            <MenuItem value={GuideStatus.DRAFT}>Draft</MenuItem>
            <MenuItem value={GuideStatus.PROFILE_INCOMPLETE}>Profile incomplete</MenuItem>
          </TextField>
        }
      />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            label="Total Profiles"
            value={stats?.totalGuideProfiles ?? '—'}
            icon={<SupervisorAccountIcon />}
            accent="primary.main"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            label="Approved Guides"
            value={approvedCount}
            icon={<VerifiedIcon />}
            accent="success.main"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            label="Pending Review"
            value={pendingCount}
            icon={<PendingActionsIcon />}
            accent="warning.main"
          />
        </Grid>
      </Grid>

      <Paper
        elevation={0}
        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2.5, overflow: 'hidden' }}
      >
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Alert severity="error" sx={{ m: 2 }}>
            Failed to load guides. Please try again.
          </Alert>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Guide</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Guide ID</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Contact</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Location</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Experience</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Rating</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Visibility</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Updated</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!data?.data?.length ? (
                    <TableRow>
                      <TableCell colSpan={10} sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
                        No guide profiles found
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.data.map((guide) => {
                      const contact = guide.user?.phone || guide.user?.email || '—';
                      const accountName = [guide.user?.firstName, guide.user?.lastName]
                        .filter(Boolean)
                        .join(' ');

                      return (
                        <TableRow
                          key={guide.id}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => openReview(guide.id)}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {guide.fullName || accountName || 'Unnamed guide'}
                            </Typography>
                            {accountName && guide.fullName && accountName !== guide.fullName && (
                              <Typography variant="caption" color="text.secondary">
                                {accountName}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {guide.identity ? (
                              <>
                                <Typography
                                  variant="body2"
                                  fontWeight={700}
                                  sx={{ fontFamily: 'ui-monospace, Menlo, Monaco, Consolas, monospace' }}
                                >
                                  {guide.identity.guideId}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {guide.identity.status}
                                </Typography>
                                <IdentityCardStateChip
                                  state={guide.identity.cardState}
                                  sx={{ display: 'flex', width: 'fit-content', mt: 0.5 }}
                                />
                              </>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                —
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{contact}</Typography>
                            {guide.user?.email && guide.user?.phone && (
                              <Typography variant="caption" color="text.secondary">
                                {guide.user.email}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {[guide.city, guide.state].filter(Boolean).join(', ') || '—'}
                          </TableCell>
                          <TableCell>
                            {guide.yearsOfExperience != null ? `${guide.yearsOfExperience} yrs` : '—'}
                          </TableCell>
                          <TableCell>
                            {guide.reviewCount ? `${guide.rating?.toFixed(1)} (${guide.reviewCount})` : '—'}
                          </TableCell>
                          <TableCell>
                            <GuideStatusChip status={guide.status} />
                          </TableCell>
                          <TableCell>
                            <StatusBadge
                              status={guide.isVisible ? 'approved' : 'pending'}
                              label={guide.isVisible ? 'Live' : 'Hidden'}
                            />
                          </TableCell>
                          <TableCell>{formatDate(guide.updatedAt)}</TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<VisibilityIcon />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openReview(guide.id);
                                }}
                              >
                                View
                              </Button>
                              {canDelete && (
                                <Button
                                  variant="contained"
                                  color="error"
                                  size="small"
                                  startIcon={<DeleteOutlineIcon />}
                                  disabled={deleteMutation.isPending}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteMutation.reset();
                                    setPendingDelete(guide);
                                  }}
                                >
                                  Delete
                                </Button>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={data?.pagination?.total ?? 0}
              page={page}
              onPageChange={(_, nextPage) => setPage(nextPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 20, 50]}
            />
          </>
        )}
      </Paper>

      <DeleteAccountDialog
        open={Boolean(pendingDelete)}
        subjectKind="guide"
        subjectName={pendingDelete ? guideName(pendingDelete) : ''}
        isDeleting={deleteMutation.isPending}
        errorMessage={deleteErrorMessage}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => pendingDelete && deleteMutation.mutate(pendingDelete.id)}
      />
    </Box>
  );
}
